import { format } from 'date-fns';
import React, { useCallback, useState } from 'react';
import { DateFormatOptions } from 'shared/types/date-format-options';
import { Timestamp } from 'shared/types/timestamp';
import {
  dateInputSeparator,
  formatDateInput,
} from 'shared/utils/format-date-input';

// The mask only restores a separator while digits follow it; a trailing one must stay deletable.
const isRestoredSeparatorAt = (value: string, index: number) =>
  value[index] === dateInputSeparator && /\d/.test(value.slice(index + 1));

export type DateInputControls = {
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => string;
  onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  reset: (date?: Timestamp) => void;
};

export const useDateInput = (
  initialValue: Timestamp | undefined,
  dateFormat: DateFormatOptions,
): DateInputControls => {
  const [value, setValue] = useState(
    initialValue ? format(initialValue, dateFormat) : '',
  );

  // Written to the DOM here: on an unchanged mask React restores the raw value after this handler.
  const onChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const input = event.target;
      const masked = formatDateInput(
        input.value,
        dateFormat,
        input.selectionStart ?? input.value.length,
      );

      input.value = masked.value;
      if (document.activeElement === input) {
        input.setSelectionRange(masked.caret, masked.caret);
      }

      setValue(masked.value);

      return masked.value;
    },
    [dateFormat],
  );

  // The mask restores a deleted separator on the same keystroke, so step over it and take the digit.
  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      const input = event.currentTarget;
      const { selectionStart, selectionEnd } = input;

      if (selectionStart === null || selectionStart !== selectionEnd) {
        return;
      }

      if (
        event.key === 'Backspace' &&
        isRestoredSeparatorAt(input.value, selectionStart - 1)
      ) {
        input.setSelectionRange(selectionStart - 1, selectionStart - 1);
        return;
      }

      if (
        event.key === 'Delete' &&
        isRestoredSeparatorAt(input.value, selectionStart)
      ) {
        input.setSelectionRange(selectionStart + 1, selectionStart + 1);
      }
    },
    [],
  );

  const reset = useCallback(
    (date?: Timestamp) => setValue(date ? format(date, dateFormat) : ''),
    [dateFormat],
  );

  return { value, onChange, onKeyDown, reset };
};
